/**
 * Foundation · Durate — formato attivo (preferenza dell'azienda di contesto).
 * Regola Foundation: azienda univoca → suo formato; nessuna azienda → centesimale.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/foundation/api/client";
import { useActiveCompany } from "@/foundation/context/ActiveCompanyContext";
import {
  DEFAULT_DURATION_FORMAT, normalizeDurationFormat, parseDuration, formatDuration,
  durationToHours, roundDurationStorage,
} from "./durationCore";

import { setActiveDurationFormat } from "./activeFormat";

const DurationFormatContext = createContext(DEFAULT_DURATION_FORMAT);
const FORMAT_EVENT = "gi:company-duration-format";

/** Da chiamare dopo il salvataggio dell'azienda: aggiorna subito il formato attivo. */
export function announceCompanyDurationFormat(companyId, format) {
  if (!companyId || !format) return;
  window.dispatchEvent(new CustomEvent(FORMAT_EVENT, { detail: { companyId, format } }));
}

export function DurationFormatProvider({ children }) {
  const { activeCompany, activeCompanyId } = useActiveCompany();
  const [fetched, setFetched] = useState({ id: "", format: DEFAULT_DURATION_FORMAT });
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    const onChange = (e) => setOverrides((o) => ({ ...o, [e.detail.companyId]: e.detail.format }));
    window.addEventListener(FORMAT_EVENT, onChange);
    return () => window.removeEventListener(FORMAT_EVENT, onChange);
  }, []);

  useEffect(() => {
    if (activeCompany || !activeCompanyId || fetched.id === activeCompanyId) return;
    let cancelled = false;
    api.get(`/companies/${activeCompanyId}`)
      .then(({ data }) => { if (!cancelled) setFetched({ id: activeCompanyId, format: data?.duration_format }); })
      .catch(() => { if (!cancelled) setFetched({ id: activeCompanyId, format: DEFAULT_DURATION_FORMAT }); });
    return () => { cancelled = true; };
  }, [activeCompany, activeCompanyId, fetched.id]);

  const format = normalizeDurationFormat(
    overrides[activeCompanyId] ? overrides[activeCompanyId]
    : activeCompany ? activeCompany.duration_format
      : (activeCompanyId && fetched.id === activeCompanyId ? fetched.format : DEFAULT_DURATION_FORMAT),
  );
  setActiveDurationFormat(format);

  // Il remount al cambio formato garantisce che nessuna schermata mostri formati misti.
  return (
    <DurationFormatContext.Provider value={format}>
      <React.Fragment key={format}>{children}</React.Fragment>
    </DurationFormatContext.Provider>
  );
}

export const useDurationFormat = () => useContext(DurationFormatContext);

/** API Foundation per i moduli: tutte le funzioni legate al formato attivo. */
export function useDuration() {
  const format = useDurationFormat();
  return useMemo(() => ({
    format,
    parse: (raw, opts) => parseDuration(raw, format, opts),
    display: (hours, opts) => formatDuration(hours, format, opts),
    toHours: (raw) => durationToHours(raw, format),
    round: (hours) => roundDurationStorage(hours, format),
  }), [format]);
}
